import React from 'react'
import {MBMemberError} from '../../../../Common/MBMemberError'
import {PaymentMethod} from '../../../../DTO/PaymentMethodDTO'
import {PAYMENT} from '../../../../../Common/Constants'
import {Spacer} from '../../../../../Common/Components/Spacer'
import {ContractTerm, ErrorMessagesBaseForContractInput, generateContractTermLabelAndValueList} from '../../../../Props/ContractInputInfo'
import {MBMemberRadioListForContractTerm} from './MBMemberRadioListForContractTerm'
import {PaymentInformation} from '../../RegisterOrganizationContentBlocks/RegisterOrganizationContentBlock'
import {MBContentNoteTypography, MBContentNoteWithMarkTypography, MBContentSubTitleTypography} from '../../../Atoms/Typographies'
import {PIMWarningMessages} from '../../../Atoms/Labels/PIMWarningMessages'
import {PIMStack} from '../../../Atoms/Layouts/Bases/PIMStack'
import {MBContentBodyDivider} from '../../../Atoms/Dividers'
import {PIMCard} from '../../../Atoms/Cards/PIMCard'

type ErrorMessageForSelectContractTerm =
  Pick<ErrorMessagesBaseForContractInput, 'paymentInputTermErrors'>

type SelectContractTermContentsProps = {
  paymentMethodList: PaymentMethod[]
  paymentInformationList: PaymentInformation[]
  contractInputInfoStateTerm: string
  onChange: (changeSelectedTerm: ContractTerm) => void
  errorMessage: ErrorMessageForSelectContractTerm
}

export const SelectContractTermContents: React.VFC<SelectContractTermContentsProps> = (
  {
    paymentMethodList,
    paymentInformationList,
    contractInputInfoStateTerm,
    onChange,
    errorMessage,
  },
) => {
  return (
    <PIMCard
      headerProps={{title: '契約単位選択', require: true}}
      testId={'block-select-contract-unit'}
    >
      <PIMStack
        spacing={'18px'}
        divider={<MBContentBodyDivider/>}
        showEndDivider
      >
        <PIMStack spacing={'12px'}>
          <MBContentSubTitleTypography>契約単位を選択してください。</MBContentSubTitleTypography>
          {errorMessage.paymentInputTermErrors.length > 0 && (
            <div data-testid={'block-select-contract-term-warning-message'}>
              <PIMWarningMessages messages={errorMessage.paymentInputTermErrors}/>
            </div>
          )}
        </PIMStack>

        <div>
          <ContractUnitRadioButtons
            paymentInformationList={paymentInformationList}
            term={contractInputInfoStateTerm}
            hasError={errorMessage.paymentInputTermErrors.length > 0}
            onChange={(changeSelectedTerm: ContractTerm): void => {
              onChange(changeSelectedTerm)
            }}
          />
          <PaymentMethodList paymentList={paymentMethodList}/>
        </div>
      </PIMStack>
    </PIMCard>
  )
}

type TermPaymentMethodPropsAddOnChange = {
  paymentInformationList: PaymentInformation[],
  term: string
  hasError: boolean
  onChange: (changeSelectedTerm: ContractTerm) => void
}

const ContractUnitRadioButtons: React.VFC<TermPaymentMethodPropsAddOnChange> = (props) => {
  const contractTermLabelAndValueList =
    generateContractTermLabelAndValueList(
      props.paymentInformationList,
    )

  return <MBMemberRadioListForContractTerm
    name={'term'}
    labelAndValues={contractTermLabelAndValueList}
    selectedValue={props.term}
    hasError={props.hasError}
    onChange={(value: ContractTerm): void => props.onChange(value)}
  />
}

type TermPaymentMethodListPair = Record<string, string[]>

const PaymentMethodList = (props: { paymentList: PaymentMethod[] }): JSX.Element => {
  const sortedTermList: string[] = Object.keys(PAYMENT.TERMS)
    .sort((a, b) => PAYMENT.TERMS[a].order - PAYMENT.TERMS[b].order)

  const paymentMethodListGroupByTerm: TermPaymentMethodListPair = {}
  sortedTermList.forEach(term => {
    paymentMethodListGroupByTerm[term] =
      getPaymentMethodsByTerm(props.paymentList, term)
        .map(paymentMethodKey => PAYMENT.METHODS[paymentMethodKey])
  })

  if (Object.keys(paymentMethodListGroupByTerm).length === 0) {
    throw new MBMemberError('SystemError')
  }

  return (
    <Spacer mt="10px">
      <MBContentNoteWithMarkTypography note={'以下の決済手段が選択できます。'}/>
      {
        Object.keys(paymentMethodListGroupByTerm).map((termKey, index) => {
          return (
            <Spacer ml="7px" key={index}>
              {
                paymentMethodListGroupByTerm[termKey].length > 0 &&
                <MBContentNoteTypography>
                  {PAYMENT.TERMS[termKey].displayLabel}：
                  {paymentMethodListGroupByTerm[termKey].join('、')}
                </MBContentNoteTypography>
              }
            </Spacer>
          )
        })
      }
    </Spacer>
  )
}

const getPaymentMethodsByTerm = (
  paymentList: PaymentMethod[],
  term: string,
): string[] =>
  Array.from(new Set(paymentList.filter(payment => payment.term === term)
    .map(payment => payment.method)))
